import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import {
  X, Sparkle, Spinner, ShieldCheck, FloppyDisk, ArrowsClockwise, ListPlus,
  ShareNetwork, Trash, Warning, CheckCircle, Plus, DownloadSimple, Copy,
} from "@phosphor-icons/react";

const inputCls = "w-full px-3 py-2 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const sectionLabel = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

function ListEditor({ label, items, onChange, testid }) {
  return (
    <div>
      <label className={sectionLabel}>{label}</label>
      <div className="space-y-1.5">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2">
            <input data-testid={`${testid}-${i}`} className={inputCls} value={v} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="press w-9 shrink-0 rounded-lg border-2 border-black bg-white flex items-center justify-center"><X size={14} weight="bold" /></button>
          </div>
        ))}
        <button data-testid={`${testid}-add`} onClick={() => onChange([...(items || []), ""])} className="press h-8 px-3 rounded-full bg-white font-bold text-xs border-2 border-black flex items-center gap-1"><Plus size={12} weight="bold" /> Add</button>
      </div>
    </div>
  );
}

export default function MeetingSummaryFlow({ meeting, onClose, onDone }) {
  const { prefs, savePrefs } = useAuth();
  const [phase, setPhase] = useState("init"); // init | privacy | generating | review | error
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [dontShow, setDontShow] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [showTasks, setShowTasks] = useState(false);
  const [taskSel, setTaskSel] = useState([]);

  const doGenerate = useCallback(async (privacyAccepted) => {
    setPhase("generating");
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/summary/generate`, { privacy_accepted: privacyAccepted });
      setSummary(data); setPhase("review");
    } catch (e) {
      if (e.response?.status === 428) { setPhase("privacy"); return; }
      setErrMsg(formatApiError(e.response?.data?.detail)); setPhase("error");
    }
  }, [meeting.id]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (prefs?.ai_summary_privacy_notice_accepted) doGenerate(true);
    else setPhase("privacy");
  }, [prefs, doGenerate]);

  const acceptPrivacy = async (cont) => {
    if (!cont) { onClose(); return; }
    if (dontShow) { try { await savePrefs({ ai_summary_privacy_notice_accepted: true }); } catch {} }
    doGenerate(true);
  };

  const setField = (k, v) => setSummary((s) => ({ ...s, [k]: v }));
  const setItem = (i, k, v) => setSummary((s) => { const items = [...s.action_items]; items[i] = { ...items[i], [k]: v }; return { ...s, action_items: items }; });

  const saveEdits = async () => {
    const payload = {
      overview: summary.overview, discussion_points: summary.discussion_points, decisions: summary.decisions,
      action_items: summary.action_items, unresolved_issues: summary.unresolved_issues, risks: summary.risks,
      deferred_topics: summary.deferred_topics, next_meeting_recommendation: summary.next_meeting_recommendation,
      draft_recap: summary.draft_recap,
    };
    await api.put(`/meetings/${meeting.id}/summary`, payload);
  };

  const save = async () => {
    try { await saveEdits(); await api.post(`/meetings/${meeting.id}/summary/approve`); toast.success("Summary saved & approved"); onDone?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const regenerate = async () => { setPhase("generating"); try { const { data } = await api.post(`/meetings/${meeting.id}/summary/regenerate`, { privacy_accepted: true }); setSummary(data); setPhase("review"); } catch (e) { setErrMsg(formatApiError(e.response?.data?.detail)); setPhase("error"); } };
  const share = async () => { try { await saveEdits(); await api.post(`/meetings/${meeting.id}/summary/approve`); const { data } = await api.post(`/meetings/${meeting.id}/summary/share`); toast.success(`Shared with ${data.shared_with} attendee(s)`); onDone?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const discard = async () => { if (!window.confirm("Discard this AI summary draft?")) return; try { await api.delete(`/meetings/${meeting.id}/summary`); toast.success("Draft discarded"); onDone?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const ownerName = (a) => users.find((u) => u.id === a.suggested_owner_id)?.name || a.suggested_owner || "Unassigned";
  const whenStr = () => meeting.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString("en-US", { timeZone: "Pacific/Honolulu" }) + " (HST)" : "";

  const buildText = () => {
    const s = summary;
    const li = (arr) => (arr && arr.length ? arr.map((x) => `  • ${x}`).join("\n") : "  —");
    const acts = (s.action_items || []).length
      ? s.action_items.map((a) => `  • ${a.title} — ${ownerName(a)}${a.suggested_due_date ? ` · due ${a.suggested_due_date}` : ""} · ${a.priority || "normal"}`).join("\n")
      : "  —";
    return [
      `${meeting.title} — Meeting Summary`, whenStr(), "",
      `OVERVIEW\n${s.overview || "—"}`, "",
      `DISCUSSION POINTS\n${li(s.discussion_points)}`, "",
      `DECISIONS\n${li(s.decisions)}`, "",
      `ACTION ITEMS\n${acts}`, "",
      `OPEN ISSUES\n${li(s.unresolved_issues)}`, "",
      `RISKS / CONCERNS\n${li(s.risks)}`, "",
      `DEFERRED TOPICS\n${li(s.deferred_topics)}`, "",
      `NEXT MEETING\n${s.next_meeting_recommendation || "—"}`, "",
      `RECAP\n${s.draft_recap || "—"}`,
    ].join("\n");
  };

  const fileBase = () => (meeting.title || "meeting-summary").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "meeting-summary";

  // Build a real PDF client-side (works on desktop + mobile; no window.open, no print dialog).
  const buildPdfDoc = () => {
    const s = summary;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const maxW = pageW - margin * 2;
    let y = margin;
    const ensure = (h) => { if (y + h > pageH - margin) { doc.addPage(); y = margin; } };
    const heading = (t) => { ensure(30); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0); doc.text(t.toUpperCase(), margin, y); y += 6; doc.setDrawColor(0); doc.setLineWidth(1); doc.line(margin, y, margin + maxW, y); y += 14; };
    const para = (t) => { doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(30); const lines = doc.splitTextToSize(String(t || "—"), maxW); lines.forEach((ln) => { ensure(15); doc.text(ln, margin, y); y += 15; }); y += 6; };
    const bullets = (arr) => { if (!arr || !arr.length) { para("—"); return; } doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(30); arr.forEach((item) => { const lines = doc.splitTextToSize("•  " + String(item), maxW - 6); lines.forEach((ln, i) => { ensure(15); doc.text(ln, margin + (i === 0 ? 0 : 12), y); y += 15; }); }); y += 6; };

    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(0);
    doc.splitTextToSize(meeting.title || "Meeting Summary", maxW).forEach((ln) => { ensure(22); doc.text(ln, margin, y); y += 22; });
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110);
    doc.text("AI Meeting Summary" + (whenStr() ? " · " + whenStr() : ""), margin, y); y += 20;

    heading("Overview"); para(s.overview);
    heading("Discussion points"); bullets(s.discussion_points);
    heading("Decisions"); bullets(s.decisions);
    heading("Action items");
    if ((s.action_items || []).length) bullets(s.action_items.map((a) => `${a.title} — ${ownerName(a)}${a.suggested_due_date ? ` · due ${a.suggested_due_date}` : ""} · ${a.priority || "normal"}`));
    else para("—");
    heading("Open issues"); bullets(s.unresolved_issues);
    heading("Risks / concerns"); bullets(s.risks);
    heading("Deferred topics"); bullets(s.deferred_topics);
    heading("Next meeting"); para(s.next_meeting_recommendation);
    heading("Recap"); para(s.draft_recap);
    return doc;
  };

  const downloadPdf = () => {
    try {
      buildPdfDoc().save(`${fileBase()}-summary.pdf`);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error("Couldn't generate the PDF. Try Copy Text instead.");
    }
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(buildText()); toast.success("Summary copied — paste it into an email, text, or doc."); }
    catch { toast.error("Couldn't copy. Try the Download button instead."); }
  };

  const shareFile = async () => {
    // Prefer sharing the actual PDF file (mobile); fall back to text share, then clipboard copy.
    try {
      const blob = buildPdfDoc().output("blob");
      const file = new File([blob], `${fileBase()}-summary.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${meeting.title} — Meeting Summary` });
        return;
      }
    } catch (e) { if (e?.name === "AbortError") return; }
    try {
      if (navigator.share) { await navigator.share({ title: `${meeting.title} — Meeting Summary`, text: buildText() }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    copyText();
  };

  const openTasks = () => { setTaskSel(summary.action_items.map((a) => ({ ...a, selected: true, assigned_to: a.suggested_owner_id || "", due_date: a.suggested_due_date || "" }))); setShowTasks(true); };
  const createTasks = async () => {
    const items = taskSel.filter((t) => t.selected).map((t) => ({ title: t.title, assigned_to: t.assigned_to || null, due_date: t.due_date || null, priority: (t.priority || "normal") }));
    if (!items.length) { toast.error("Select at least one action item"); return; }
    try { const { data } = await api.post(`/meetings/${meeting.id}/summary/create-tasks`, { items }); toast.success(`Created ${data.created} task(s)`); setShowTasks(false); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto p-4 bg-black/60" onClick={phase === "review" ? undefined : onClose}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        data-testid="meeting-summary-flow" className="relative bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-2xl my-6">
        <div className="sticky top-0 bg-[#F3E8FF] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><Sparkle size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-lg font-bold">AI Meeting Summary</h2>
          </div>
          <button data-testid="summary-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        {phase === "privacy" && (
          <div className="p-6 space-y-4" data-testid="privacy-notice">
            <div className="w-11 h-11 rounded-xl bg-[#FEF9C3] border-2 border-black flex items-center justify-center"><ShieldCheck size={24} weight="bold" /></div>
            <p className="text-sm text-black/80">AI Meeting Summary will process the meeting notes, transcript, and selected attachments to create a summary. Do you want to continue?</p>
            <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" data-testid="privacy-dont-show" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} className="w-4 h-4" /> Don't show again</label>
            <div className="flex gap-3">
              <button data-testid="privacy-continue" onClick={() => acceptPrivacy(true)} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black">Continue</button>
              <button data-testid="privacy-cancel" onClick={() => acceptPrivacy(false)} className="press flex-1 h-11 rounded-full bg-white font-bold border-2 border-black">Cancel</button>
            </div>
          </div>
        )}

        {phase === "generating" && (
          <div className="p-12 text-center" data-testid="summary-generating">
            <Spinner size={40} className="mx-auto animate-spin text-secondary" />
            <p className="font-display text-xl font-bold mt-4">Generating summary…</p>
            <p className="text-sm text-black/50">Analyzing notes, transcript & selected attachments.</p>
          </div>
        )}

        {phase === "error" && (
          <div className="p-8 text-center" data-testid="summary-error">
            <Warning size={40} className="mx-auto text-primary" />
            <p className="font-bold mt-3">{errMsg || "Something went wrong."}</p>
            <button onClick={() => doGenerate(true)} className="press mt-4 h-10 px-5 rounded-full bg-primary text-white font-bold border-2 border-black">Retry</button>
          </div>
        )}

        {phase === "review" && summary && !showTasks && (
          <div className="p-5 space-y-4" data-testid="summary-review">
            <div className="rounded-lg bg-[#DCFCE7] border-2 border-black px-3 py-2 text-xs font-bold flex items-center gap-2"><CheckCircle size={14} weight="fill" /> Review & edit before saving. Nothing is shared or assigned without your approval.</div>

            <div><label className={sectionLabel}>Meeting overview</label><textarea data-testid="summary-overview" className={inputCls + " h-24 resize-none"} value={summary.overview} onChange={(e) => setField("overview", e.target.value)} /></div>
            <ListEditor label="Main discussion points" items={summary.discussion_points} onChange={(v) => setField("discussion_points", v)} testid="summary-discussion" />
            <ListEditor label="Decisions made" items={summary.decisions} onChange={(v) => setField("decisions", v)} testid="summary-decisions" />

            <div>
              <label className={sectionLabel}>Action items (suggested owners & dates — review before assigning)</label>
              <div className="space-y-2">
                {summary.action_items.map((a, i) => (
                  <div key={i} data-testid={`summary-action-${i}`} className="rounded-lg border-2 border-black bg-white p-3 space-y-2">
                    <input className={inputCls} value={a.title} onChange={(e) => setItem(i, "title", e.target.value)} />
                    <div className="grid grid-cols-3 gap-2">
                      <select data-testid={`summary-action-owner-${i}`} className={inputCls} value={a.suggested_owner_id || ""} onChange={(e) => setItem(i, "suggested_owner_id", e.target.value)}>
                        <option value="">Owner…</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      <input type="date" className={inputCls} value={a.suggested_due_date || ""} onChange={(e) => setItem(i, "suggested_due_date", e.target.value)} />
                      <select className={inputCls} value={a.priority} onChange={(e) => setItem(i, "priority", e.target.value)}>
                        {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {summary.action_items.length === 0 && <p className="text-sm text-black/40">No action items identified.</p>}
              </div>
            </div>

            <ListEditor label="Open issues" items={summary.unresolved_issues} onChange={(v) => setField("unresolved_issues", v)} testid="summary-issues" />
            <ListEditor label="Risks or concerns" items={summary.risks} onChange={(v) => setField("risks", v)} testid="summary-risks" />
            <ListEditor label="Deferred topics" items={summary.deferred_topics} onChange={(v) => setField("deferred_topics", v)} testid="summary-deferred" />
            <div><label className={sectionLabel}>Next meeting recommendation</label><textarea className={inputCls + " h-16 resize-none"} value={summary.next_meeting_recommendation} onChange={(e) => setField("next_meeting_recommendation", e.target.value)} /></div>
            <div><label className={sectionLabel}>Draft meeting recap</label><textarea data-testid="summary-recap" className={inputCls + " h-28 resize-none"} value={summary.draft_recap} onChange={(e) => setField("draft_recap", e.target.value)} /></div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
              <button data-testid="summary-save-btn" onClick={save} className="press h-11 rounded-full bg-primary text-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><FloppyDisk size={16} weight="bold" /> Save Summary</button>
              <button data-testid="summary-download-btn" onClick={downloadPdf} className="press h-11 rounded-full bg-secondary text-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><DownloadSimple size={16} weight="bold" /> Download PDF</button>
              <button data-testid="summary-copy-btn" onClick={copyText} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><Copy size={16} weight="bold" /> Copy Text</button>
              <button data-testid="summary-sharefile-btn" onClick={shareFile} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><ShareNetwork size={16} weight="bold" /> Send / Share</button>
              <button data-testid="summary-regenerate-btn" onClick={regenerate} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><ArrowsClockwise size={16} weight="bold" /> Regenerate</button>
              <button data-testid="summary-create-tasks-btn" onClick={openTasks} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><ListPlus size={16} weight="bold" /> Create Tasks</button>
              <button data-testid="summary-share-btn" onClick={share} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><CheckCircle size={16} weight="bold" /> Notify Team</button>
              <button data-testid="summary-discard-btn" onClick={discard} className="press h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-1.5 text-sm"><Trash size={16} weight="bold" /> Discard</button>
            </div>
            <p className="text-xs text-black/50 pt-1"><strong>Save Summary</strong> keeps it in this meeting (viewable in the app). <strong>Download PDF</strong> / <strong>Copy Text</strong> / <strong>Send / Share</strong> let you save the file to your device or send it to anyone. <strong>Notify Team</strong> alerts meeting attendees in-app.</p>
          </div>
        )}

        {phase === "review" && showTasks && (
          <div className="p-5 space-y-3" data-testid="summary-create-tasks-panel">
            <p className="font-bold text-sm">Create tasks from action items — review owners & dates. Employees are only notified after you confirm.</p>
            {taskSel.map((t, i) => (
              <div key={i} className="rounded-lg border-2 border-black bg-white p-3 space-y-2">
                <label className="flex items-center gap-2 font-bold text-sm"><input type="checkbox" checked={t.selected} onChange={(e) => setTaskSel((s) => s.map((x, j) => j === i ? { ...x, selected: e.target.checked } : x))} className="w-4 h-4" /> {t.title}</label>
                <div className="grid grid-cols-2 gap-2">
                  <select className={inputCls} value={t.assigned_to} onChange={(e) => setTaskSel((s) => s.map((x, j) => j === i ? { ...x, assigned_to: e.target.value } : x))}>
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <input type="date" className={inputCls} value={t.due_date} onChange={(e) => setTaskSel((s) => s.map((x, j) => j === i ? { ...x, due_date: e.target.value } : x))} />
                </div>
              </div>
            ))}
            <div className="flex gap-3">
              <button data-testid="confirm-create-tasks" onClick={createTasks} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black">Create selected tasks</button>
              <button onClick={() => setShowTasks(false)} className="press flex-1 h-11 rounded-full bg-white font-bold border-2 border-black">Back</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
